import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import Table from "components/DesignSystem/Table/V2/Table";
import { TransactionComboboxControl } from "components/Transaction/TransactionColumn";
import { useToast } from "hooks/useToast";
import { useMemo } from "react";
import { MultiValue, SingleValue } from "react-select";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { useUpdateBankAccountantAccessMutation } from "store/apis/bankConnections";
import { ENTITY_BANK_CONNECTION_DETAILS } from "types/Models/bookkeepingTracker";
import { BackendError } from "types/utils/error";
import { BankConnectionStatus } from "./BankConnectionStatus";

const bankAccessOptions = [
  {
    label: (
      <div className="t-flex t-gap-1 t-items-center">
        <div className="t-h-1.5 t-w-1.5 t-rounded-full t-bg-dark_green" />
        <div className="">Yes</div>
      </div>
    ),
    value: "true",
  },
  {
    label: (
      <div className="t-flex t-gap-1 t-items-center">
        <div className="t-h-1.5 t-w-1.5 t-rounded-full t-bg-red" />
        <div className="">No</div>
      </div>
    ),
    value: "false",
  },
];

export const EntityBankConnections = ({
  connectionDetails,
  entityId,
}: {
  connectionDetails: ENTITY_BANK_CONNECTION_DETAILS[];
  entityId: string;
}) => {
  const { alertToast, successToast } = useToast();
  const [updateBankAccountantAccess] = useUpdateBankAccountantAccessMutation();

  const handleBankAccessUpdate = async (
    entityItemId: string,
    accountantAccess?: string
  ) => {
    try {
      await updateBankAccountantAccess({
        entityId,
        accountantAccess: accountantAccess === "true",
        entityItemId: entityItemId,
      }).unwrap();
      successToast({ message: "Access updated successfully" });
    } catch (e) {
      alertToast({ message: (e as BackendError).data?.error?.message });
    }
  };

  const createColumn = createColumnHelper<ENTITY_BANK_CONNECTION_DETAILS>();

  const columns = useMemo(
    () => [
      createColumn.accessor("name", {
        header: "Banks",
        size: 30,
        cell: (info) => (
          <div className="t-text-subtext t-text-text-100">
            {info.getValue()}
          </div>
        ),
      }),
      createColumn.accessor("connection_available", {
        header: () => "Connections",
        size: 30,
        cell: (info) => (
          <div>
            <BankConnectionStatus connected={info.getValue()} />
          </div>
        ),
      }),
      createColumn.accessor("accountant_access", {
        header: () => (
          <div className="t-w-full t-flex t-justify-end">Accountant Access</div>
        ),
        size: 40,
        cell: (info) => (
          <div className="t-w-20 t-ml-auto">
            <Combobox
              size="small"
              menuPlacement="auto"
              creatable={false}
              onChange={(
                prop: SingleValue<OptionData> | MultiValue<OptionData>
              ) =>
                handleBankAccessUpdate(
                  info.row.original.entity_item_id,
                  (prop as SingleValue<OptionData>)?.value
                )
              }
              components={{
                Control: TransactionComboboxControl,
                ClearIndicator: () => null,
              }}
              options={bankAccessOptions}
              defaultValue={
                bankAccessOptions.find(
                  (option) => option.value == info.getValue()?.toString()
                ) || null
              }
              menuPortalTarget={document.body}
            />
          </div>
        ),
      }),
    ],
    [connectionDetails]
  );

  const data = useMemo(() => connectionDetails, [connectionDetails]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: 25,
    },
    enableRowSelection: true,
  });

  if (!data?.length) {
    return (
      <div className="t-flex t-h-40 t-w-full t-justify-center t-items-center">
        <div className="t-text-body">No connections</div>
      </div>
    );
  }

  return (
    <Table.Container className="t-h-full">
      <Table.Content>
        <Table.Head className="t-border-b-0">
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.HeadRow key={headerGroup.id} style={{ border: "0px" }}>
              {headerGroup.headers.map((header) => (
                <Table.HeadCell
                  className="t-text-body-sm t-p-2 t-group t-normal-case"
                  key={header.id}
                  style={{ width: `${header.getSize()}%` }}
                >
                  <span className="t-flex t-gap-1 t-items-center">
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </span>
                </Table.HeadCell>
              ))}
            </Table.HeadRow>
          ))}
        </Table.Head>
        <Table.Body>
          {table.getRowModel().rows.map((row) => (
            <Table.Row key={row.id} className="t-border-b-0">
              {row.getVisibleCells().map((cell) => {
                return (
                  <Table.Cell
                    key={cell.id}
                    style={{
                      width: `${cell.column.getSize()}%`,
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Cell>
                );
              })}
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Content>
    </Table.Container>
  );
};
